class CartDiscountCode extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.input = null;
    this.button = null;
    this.removeButton = null;
    this.appliedWrap = null;
    this.appliedCodeElement = null;
    this.message = null;

    this.originalButtonText = '';
    this.originalRemoveText = '';
    this.isSubmitting = false;

    this.onSubmit = this.onSubmit.bind(this);
    this.onRemove = this.onRemove.bind(this);
  }

  connectedCallback() {
    if (this.dataset.kdcReady === 'true') return;

    this.form = this.querySelector('[data-kdc-form]');
    this.input = this.querySelector('[data-kdc-input]');
    this.button = this.querySelector('[data-kdc-submit]');
    this.removeButton = this.querySelector('[data-kdc-remove]');
    this.appliedWrap = this.querySelector('[data-kdc-applied]');
    this.appliedCodeElement = this.querySelector('[data-kdc-applied-code]');
    this.message = this.querySelector('[data-kdc-message]');

    if (!this.form || !this.input || !this.button || !this.message) {
      return;
    }

    this.dataset.kdcReady = 'true';
    this.originalButtonText = this.button.textContent.trim();

    if (this.removeButton) {
      this.originalRemoveText = this.removeButton.textContent.trim();
    }

    this.form.addEventListener('submit', this.onSubmit);

    if (this.removeButton) {
      this.removeButton.addEventListener('click', this.onRemove);
    }

    const liquidAppliedCode = String(
      this.dataset.appliedCode || ''
    ).trim();

    /*
      Liquid/Shopify cart data is the source of truth for the visible
      "applied" state. Stored data is never shown as applied by itself.
    */
    if (liquidAppliedCode) {
      /*
        Show any discount code that Shopify reports as applied, but do not
        automatically claim ownership of codes that may have been applied
        elsewhere (for example through another app, link, or checkout flow).

        Codes entered through this component are already stored explicitly in
        onSubmit(), so only those codes are eligible for the automatic cleanup
        logic below.
      */
      this.setAppliedCode(liquidAppliedCode);
    } else {
      this.setAppliedCode('');
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit);
    }

    if (this.removeButton) {
      this.removeButton.removeEventListener('click', this.onRemove);
    }
  }

  static get storageKey() {
    return 'kinuvel_manual_discount_code';
  }

  static getRoot() {
    return (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) || '/';
  }

  static getStoredManualCode() {
    try {
      return sessionStorage.getItem(
        CartDiscountCode.storageKey
      ) || '';
    } catch (error) {
      return '';
    }
  }

  static storeManualCode(code) {
    try {
      if (code) {
        sessionStorage.setItem(
          CartDiscountCode.storageKey,
          code
        );
      } else {
        sessionStorage.removeItem(
          CartDiscountCode.storageKey
        );
      }
    } catch (error) {
      /*
        Strict privacy modes can block storage. The Shopify cart continues
        to work because storage is not the source of truth for discounts.
      */
    }
  }

  normalizeCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  getAppliedDiscountTitles(cart) {
    const titles = [];

    const pushTitle = (value) => {
      const normalized = this.normalizeCode(value);

      if (normalized && !titles.includes(normalized)) {
        titles.push(normalized);
      }
    };

    (cart.cart_level_discount_applications || []).forEach(
      (application) => {
        if (!application || application.type === 'automatic') return;
        pushTitle(application.title);
      }
    );

    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        if (!discount) return;
        pushTitle(discount.title);
      });

      (item.line_level_discount_allocations || []).forEach(
        (allocation) => {
          const application =
            allocation && allocation.discount_application;

          if (!application || application.type === 'automatic') return;

          pushTitle(application.title);
        }
      );
    });

    return titles;
  }

  codeIsApplied(cart, code) {
    const normalizedCode = this.normalizeCode(code);

    if (!normalizedCode) return false;

    return this
      .getAppliedDiscountTitles(cart)
      .includes(normalizedCode);
  }

  setLoading(loading, mode = 'apply') {
    this.isSubmitting = loading;

    this.button.disabled = loading;
    this.input.disabled = loading;

    this.button.textContent =
      loading && mode === 'apply'
        ? this.dataset.loadingText || this.originalButtonText
        : this.originalButtonText;

    if (this.removeButton) {
      this.removeButton.disabled = loading;
      this.removeButton.textContent =
        loading && mode === 'remove'
          ? this.dataset.removingText || this.originalRemoveText
          : this.originalRemoveText;
    }

    this.setAttribute(
      'aria-busy',
      loading ? 'true' : 'false'
    );
  }

  showMessage(text, type) {
    this.message.textContent = text || '';
    this.message.classList.toggle(
      'is-success',
      type === 'success'
    );
    this.message.classList.toggle(
      'is-error',
      type === 'error'
    );
  }

  openPanel() {
    const details = this.closest('details');

    if (details) {
      details.open = true;
    }
  }

  setAppliedCode(code) {
    if (!this.appliedWrap || !this.appliedCodeElement) return;

    const cleanCode = String(code || '').trim();

    this.appliedCodeElement.textContent = cleanCode;
    this.appliedWrap.hidden = !cleanCode;
  }

  renderUpdatedCart(cart) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (
      cartDrawer &&
      typeof cartDrawer.renderContents === 'function' &&
      cart.sections
    ) {
      cartDrawer.renderContents(cart);
    }
  }

  findCurrentComponent() {
    return document.querySelector(
      'cart-drawer cart-discount-code'
    );
  }

  async updateDiscount(discountValue, includeSections = true) {
    const body = {
      discount: discountValue,
    };

    if (includeSections) {
      body.sections = [
        'cart-drawer',
        'cart-icon-bubble',
      ];

      body.sections_url =
        window.location.pathname +
        window.location.search;
    }

    const response = await fetch(
      CartDiscountCode.getRoot() + 'cart/update.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      }
    );

    const cart = await response.json();

    if (!response.ok) {
      throw new Error(
        cart.description ||
        cart.message ||
        'Cart discount request failed'
      );
    }

    return cart;
  }

  async onSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) return;

    const code = this.input.value.trim();

    if (!code) {
      this.openPanel();
      this.input.focus();
      return;
    }

    this.showMessage('', '');
    this.setLoading(true, 'apply');

    try {
      const cart = await this.updateDiscount(code);
      const applied = this.codeIsApplied(cart, code);

      if (!applied) {
        /*
          Ajax Cart API returns the updated cart, but it does not provide a
          reliable detailed reason such as "code does not exist" versus
          "code is not eligible". Do not invent a reason.
        */
        CartDiscountCode.storeManualCode('');
        this.setLoading(false, 'apply');
        this.openPanel();
        this.setAppliedCode('');

        /*
          Keep the customer's text in the field so a typo can be corrected.
        */
        this.input.value = code;

        this.showMessage(
          this.dataset.notAppliedText ||
            'This discount code is invalid or not applicable to this cart.',
          'error'
        );

        return;
      }

      CartDiscountCode.storeManualCode(code);
      this.setLoading(false, 'apply');
      this.renderUpdatedCart(cart);

      window.requestAnimationFrame(() => {
        const current = this.findCurrentComponent();

        if (!current) return;

        current.openPanel();
        current.setAppliedCode(code);
        current.input.value = '';

        current.showMessage(
          current.dataset.successText ||
            'Discount code applied.',
          'success'
        );
      });
    } catch (error) {
      this.setLoading(false, 'apply');
      this.openPanel();

      /*
        Technical/network errors also preserve the entered code.
      */
      this.input.value = code;

      this.showMessage(
        this.dataset.errorText ||
          'We could not check the discount code. Please try again.',
        'error'
      );
    }
  }

  async onRemove(event) {
    event.preventDefault();

    if (this.isSubmitting) return;

    this.showMessage('', '');
    this.setLoading(true, 'remove');

    try {
      const cart = await this.updateDiscount('');

      CartDiscountCode.storeManualCode('');
      this.setLoading(false, 'remove');
      this.renderUpdatedCart(cart);

      window.requestAnimationFrame(() => {
        const current = this.findCurrentComponent();

        if (!current) return;

        current.openPanel();
        current.setAppliedCode('');
        current.input.value = '';

        current.showMessage(
          current.dataset.removedText ||
            'Discount code removed.',
          'success'
        );
      });
    } catch (error) {
      this.setLoading(false, 'remove');
      this.openPanel();

      this.showMessage(
        this.dataset.errorText ||
          'We could not update the discount code. Please try again.',
        'error'
      );
    }
  }
}

if (!customElements.get('cart-discount-code')) {
  customElements.define(
    'cart-discount-code',
    CartDiscountCode
  );
}

/*
  Keep a manually-entered code only while Shopify still reports it as
  applicable to the current cart.

  If the last item is removed, or a cart change makes that manual code stop
  applying, clear it from Shopify. This prevents an old code from silently
  reappearing on a later purchase.
*/
(function bindKinuvelDiscountCartSync() {
  if (window.__kinuvelDiscountCartSyncBound) return;

  if (
    typeof subscribe !== 'function' ||
    typeof PUB_SUB_EVENTS === 'undefined' ||
    !PUB_SUB_EVENTS.cartUpdate
  ) {
    return;
  }

  window.__kinuvelDiscountCartSyncBound = true;

  let clearing = false;

  const getAppliedTitles = (cart) => {
    const titles = [];
    const normalize = (value) =>
      String(value || '').trim().toUpperCase();

    const push = (value) => {
      const normalized = normalize(value);

      if (normalized && !titles.includes(normalized)) {
        titles.push(normalized);
      }
    };

    (cart.cart_level_discount_applications || []).forEach(
      (application) => {
        if (!application || application.type === 'automatic') return;
        push(application.title);
      }
    );

    (cart.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => {
        if (discount) push(discount.title);
      });

      (item.line_level_discount_allocations || []).forEach(
        (allocation) => {
          const application =
            allocation && allocation.discount_application;

          if (!application || application.type === 'automatic') return;

          push(application.title);
        }
      );
    });

    return titles;
  };

  subscribe(
    PUB_SUB_EVENTS.cartUpdate,
    async (event) => {
      if (
        clearing ||
        !event ||
        !event.cartData
      ) {
        return;
      }

      const storedCode =
        CartDiscountCode.getStoredManualCode();

      if (!storedCode) {
        return;
      }

      const cart = event.cartData;
      const normalizedStoredCode =
        String(storedCode).trim().toUpperCase();

      const stillApplied =
        getAppliedTitles(cart).includes(
          normalizedStoredCode
        );

      /*
        Empty cart: always clear the drawer-entered code.
        Non-empty cart: clear it only if Shopify no longer reports it as
        applied after the customer's cart change.
      */
      if (
        Number(cart.item_count) > 0 &&
        stillApplied
      ) {
        return;
      }

      clearing = true;

      try {
        await fetch(
          CartDiscountCode.getRoot() + 'cart/update.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              discount: '',
            }),
          }
        );

        CartDiscountCode.storeManualCode('');
      } catch (error) {
        /*
          Do not block the normal cart update if the cleanup request fails.
        */
      } finally {
        clearing = false;
      }
    }
  );
})();
